import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { PBPUTILS } from "../../config/API";
import jsPDF from 'jspdf';
import SuccessPage from '../SuccessPage';
import { withSanitizedFileName } from "../../utils/sanitizeFileName";

const SalesTaxReport = () => {
  // Function to get default month and year
  const getDefaultMonthYear = () => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // 0-11 (January is 0)
    const currentYear = currentDate.getFullYear();

    let defaultMonth, defaultYear;

    if (currentMonth === 0) { // January
      defaultMonth = 'December';
      defaultYear = (currentYear - 1).toString();
    } else {
      // Get previous month
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      defaultMonth = monthNames[currentMonth - 1];
      defaultYear = currentYear.toString();
    }

    return { defaultMonth, defaultYear };
  };

  const [formData, setFormData] = useState({
    // Income - 4000 Sales
    '4010-Sales Compact Discount Tobacco (NPM)': { gross: '0.00', taxable: '0.00' },
    '4020-Sales Compact Premium Tobacco PM & NPM': { gross: '0.00', taxable: '0.00' },
    '4025-Sales Other Tobacco Products': { gross: '0.00', taxable: '0.00' },
    '4030-Sales Fuel': { gross: '0.00', taxable: '0.00' }, // Always 0
    '4035-Sales Diesel Fuel': { gross: '0.00', taxable: '0.00' }, // Always 0
    '4040-Sales Gift Shop': { gross: '0.00', taxable: '0.00' },
    '4050-Sales Merchandise': { gross: '0.00', taxable: '0.00' },
    '4055-Non-Inv Merchandise': { gross: '0.00', taxable: '0.00' },

    // Revenue - 4100
    '4110-Revenue ATM': { gross: '0.00', taxable: '0.00' },

    // Sales Discounts & Returns - 4900
    '4910-Sales Discounts': { gross: '0.00', taxable: '0.00' },
    '4920-Sales coupons': { gross: '0.00', taxable: '0.00' }
  });

  const getBusinessName = () => {
    const storedBusinessName = localStorage.getItem("selectedBusinessName");
    return storedBusinessName || 'Potawatomi Retail - One Stop';
  };

  // Retailer Information State
  const [retailerInfo, setRetailerInfo] = useState({
    retailer: getBusinessName(),
    month: getDefaultMonthYear().defaultMonth,
    year: getDefaultMonthYear().defaultYear,
    expirationDate: ''
  });

  // Taxpayer ID state
  const [taxpayerId, setTaxpayerId] = useState('107');
  const [businessName, setBusinessName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [submissionData, setSubmissionData] = useState(null);
  const [signatureDataURL, setSignatureDataURL] = useState('');
  const [attachments, setAttachments] = useState([]);

  // Printed Name and Title State
  const [printedName, setPrintedName] = useState('');
  const [title, setTitle] = useState('');

  // Signature pad state and ref
  const sigCanvas = useRef(null);
  const [isSigned, setIsSigned] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [currentDate] = useState(new Date().toLocaleDateString());

  // Month options for dropdown
  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Year options (current year and previous years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 6 }, (_, i) => (currentYear - 3 + i).toString());

  // Get taxpayer ID and business name from localStorage on component mount
  useEffect(() => {
    const storedTaxpayerId = localStorage.getItem("selectedTaxPayer");
    const storedBusinessName = localStorage.getItem("selectedBusinessName");
    const storedExpirationDate = localStorage.getItem("businessLicenseExpirationDate");

    if (storedTaxpayerId) {
      setTaxpayerId(storedTaxpayerId);
      console.log('Retrieved taxpayer ID from localStorage:', storedTaxpayerId);
    } else {
      console.warn('No taxpayer ID found in localStorage');
      // Fallback to default if not found
      setTaxpayerId('107');
    }

    if (storedBusinessName) {
      setBusinessName(storedBusinessName);
      console.log('Retrieved business name from localStorage:', storedBusinessName);
    }

    if (storedExpirationDate) {
      setRetailerInfo(prev => ({
        ...prev,
        expirationDate: storedExpirationDate
      }));
    }
  }, []);

  // Initialize canvas size after component mounts
  useEffect(() => {
    const updateCanvasSize = () => {
      const container = document.getElementById('signature-container');
      if (container) {
        const width = container.clientWidth - 32;
        const height = 200;
        setCanvasSize({ width, height });

        if (sigCanvas.current) {
          const isEmpty = sigCanvas.current.isEmpty();
          if (!isEmpty) {
            const data = sigCanvas.current.toData();
            sigCanvas.current.clear();
            setTimeout(() => {
              if (sigCanvas.current) {
                sigCanvas.current.fromData(data);
              }
            }, 0);
          }
        }
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, []);

  const handleInputChange = (category, field, value) => {
    const nextValue =
      value === '' ? '' : String(Math.max(0, Number(value)));
    setFormData(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: nextValue
      }
    }));
  };

  // Handle retailer information changes
  const handleRetailerInfoChange = (field, value) => {
    setRetailerInfo(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // File upload handlers for Supporting Documents
  const handleAttachmentChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;

    setAttachments((prev) => {
      const existingNames = new Set(prev.map((f) => f.name));
      const newFiles = files.filter((f) => !existingNames.has(f.name));
      return [...prev, ...newFiles];
    });

    event.target.value = '';
  };

  const handleRemoveAttachment = (indexToRemove) => {
    setAttachments((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  // Calculate totals
  const calculateCategoryTotal = (categories) => {
    return categories.reduce((total, category) => {
      const gross = parseFloat(formData[category]?.gross) || 0;
      return total + gross;
    }, 0);
  };

  const calculateTaxableTotal = (categories) => {
    return categories.reduce((total, category) => {
      const taxable = parseFloat(formData[category]?.taxable) || 0;
      return total + taxable;
    }, 0);
  };

  const calculateSalesTax = (taxableAmount) => {
    return (taxableAmount * 0.06).toFixed(2);
  };

  // Category groups
  const sales4000Categories = [
    '4010-Sales Compact Discount Tobacco (NPM)',
    '4020-Sales Compact Premium Tobacco PM & NPM',
    '4025-Sales Other Tobacco Products',
    '4030-Sales Fuel',
    '4035-Sales Diesel Fuel',
    '4040-Sales Gift Shop',
    '4050-Sales Merchandise',
    '4055-Non-Inv Merchandise'
  ];

  const revenue4100Categories = [
    '4110-Revenue ATM'
  ];

  const discounts4900Categories = [
    '4910-Sales Discounts',
    '4920-Sales coupons'
  ];

  // Calculate totals
  const total4000Sales = calculateCategoryTotal(sales4000Categories);
  const total4000Taxable = calculateTaxableTotal(sales4000Categories);
  const total4000Tax = calculateSalesTax(total4000Taxable);

  const total4100Revenue = calculateCategoryTotal(revenue4100Categories);
  const total4100Taxable = calculateTaxableTotal(revenue4100Categories);
  const total4100Tax = calculateSalesTax(total4100Taxable);

  const total4900Discounts = calculateCategoryTotal(discounts4900Categories);
  const total4900Taxable = calculateTaxableTotal(discounts4900Categories);
  const total4900Tax = calculateSalesTax(total4900Taxable);

  const totalIncome = total4000Sales + total4100Revenue + total4900Discounts;
  const totalTaxable = total4000Taxable + total4100Taxable + total4900Taxable;
  const totalSalesTax = (parseFloat(total4000Tax) + parseFloat(total4100Tax) + parseFloat(total4900Tax)).toFixed(2);

  // Compact Tobacco Sales Tax (4010 & 4020)
  const compactTobaccoTaxable = calculateTaxableTotal([
    '4010-Sales Compact Discount Tobacco (NPM)',
    '4020-Sales Compact Premium Tobacco PM & NPM'
  ]);
  const compactTobaccoTax = calculateSalesTax(compactTobaccoTaxable);

  // All other Sales Tax
  const otherTaxable = totalTaxable - compactTobaccoTaxable;
  const otherTax = calculateSalesTax(otherTaxable);

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Helper function to convert data URL to File
  const dataURLtoFile = (dataurl, filename) => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };

  // Add this helper function to convert PDF to Blob
  const pdfToBlob = (pdf) => {
    const pdfOutput = pdf.output('blob');
    return pdfOutput;
  };

  // Generate PDF function - COMPLETELY REWRITTEN VERSION - accepts optional signature override (use when generating for send, since setState is async)
  const generatePDF = (signatureDataURLOverride) => {
    const pdf = new jsPDF();
    const signatureToUse = signatureDataURLOverride !== undefined ? signatureDataURLOverride : signatureDataURL;
    let yPosition = 20;
    const pageHeight = pdf.internal.pageSize.height;
    const margin = 20;

    // Function to check if we need a new page
    const checkPageBreak = (requiredSpace = 10) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        pdf.addPage();
        yPosition = 20;
        return true;
      }
      return false;
    };

    // Set font and colors
    pdf.setFont('helvetica');
    pdf.setTextColor(0, 0, 0);

    // Header Section
    pdf.setFontSize(16);
    pdf.text('Monthly Retailer Sales Tax Report', 105, yPosition, { align: 'center' });
    yPosition += 10;

    pdf.setFontSize(12);
    pdf.text('Prairie Band Potawatomi Tax Commission', 105, yPosition, { align: 'center' });
    yPosition += 6;
    pdf.text('16281 Q Road', 105, yPosition, { align: 'center' });
    yPosition += 6;
    pdf.text('Mayetta, KS 66509', 105, yPosition, { align: 'center' });
    yPosition += 8;

    pdf.setFontSize(10);
    pdf.text('Reports and Payment due on the 10th of each Month', 105, yPosition, { align: 'center' });
    yPosition += 15;

    // Retailer Information
    checkPageBreak(25);
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Retailer Information:', margin, yPosition);
    yPosition += 8;

    pdf.setFontSize(10);
    pdf.text(`Retailer: ${retailerInfo.retailer}`, margin, yPosition);
    yPosition += 6;
    pdf.text(`Month: ${retailerInfo.month} ${retailerInfo.year}`, margin, yPosition);
    yPosition += 6;

    const expirationText = `PBPN Tribal Tobacco Retailer License Expiration Date: ${retailerInfo.expirationDate}`;
    const expirationLines = pdf.splitTextToSize(expirationText, 170);
    expirationLines.forEach(line => {
      pdf.text(line, margin, yPosition);
      yPosition += 6;
    });

    if (taxpayerId) {
      pdf.text(`Taxpayer ID: ${taxpayerId}`, margin, yPosition);
      yPosition += 6;
    }
    // if (businessName) {
    //   pdf.text(`Business: ${businessName}`, margin, yPosition);
    //   yPosition += 6;
    // }

    yPosition += 10;

    // Sales Tax Report Data
    checkPageBreak(15);
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Sales Tax Report Summary', margin, yPosition);
    yPosition += 10;

    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);

    // Table Headers
    checkPageBreak(15);
    pdf.text('Category', margin, yPosition);
    pdf.text('Gross Amount', 96, yPosition, { align: 'center' });
    pdf.text('Taxable Amount', 120, yPosition);
    pdf.text('6% Sales Tax', 165, yPosition);
    yPosition += 8;

    // Draw line under headers
    pdf.line(margin, yPosition, 190, yPosition);
    yPosition += 8;

    // NEW: Improved function to render category rows
    const renderCategoryRow = (category, displayName) => {
      const categoryData = formData[category];
      const gross = categoryData?.gross || '0.00';
      const taxable = categoryData?.taxable || '0.00';
      const tax = calculateSalesTax(parseFloat(taxable));

      // Split the display name into lines
      const nameLines = pdf.splitTextToSize(displayName, 45);
      const lineHeight = 6;
      const requiredHeight = nameLines.length * lineHeight + 4;

      // Check if we need a new page
      checkPageBreak(requiredHeight);

      // Store the starting Y position for this row
      const rowStartY = yPosition;

      // Draw all text lines
      nameLines.forEach((line, index) => {
        pdf.text(line, margin + 5, yPosition);
        yPosition += lineHeight;
      });

      // Calculate the Y position for numbers (middle of the text block)
      const numbersY = rowStartY + ((nameLines.length - 1) * lineHeight);

      // Draw numbers at the calculated Y position
      pdf.text('$' + formatCurrency(parseFloat(gross)), 105, numbersY, { align: 'right' });
      pdf.text('$' + formatCurrency(parseFloat(taxable)), 145, numbersY, { align: 'right' });
      pdf.text('$' + formatCurrency(parseFloat(tax)), 185, numbersY, { align: 'right' });

      // Move to next row position
      yPosition += 4;
    };

    // 4000-Sales Section
    checkPageBreak(10);

    pdf.text('4000-Sales', margin, yPosition);
    yPosition += 8;

    sales4000Categories.forEach(category => {
      const categoryName = category.split('-')[1] || category;
      renderCategoryRow(category, categoryName);
    });

    // 4000-Sales Total
    checkPageBreak(12);
    yPosition += 4;

    pdf.text('Total', margin, yPosition);
    pdf.text('$' + formatCurrency(total4000Sales), 105, yPosition, { align: 'right' });
    pdf.text('$' + formatCurrency(total4000Taxable), 145, yPosition, { align: 'right' });
    pdf.text('$' + formatCurrency(parseFloat(total4000Tax)), 185, yPosition, { align: 'right' });
    yPosition += 12;

    // 4100-Revenue Section
    checkPageBreak(10);

    pdf.text('4100-Revenue', margin, yPosition);
    yPosition += 8;

    revenue4100Categories.forEach(category => {
      const categoryName = category.split('-')[1] || category;
      renderCategoryRow(category, categoryName);
    });

    // 4100-Revenue Total
    checkPageBreak(12);
    yPosition += 4;

    pdf.text('Total', margin, yPosition);
    pdf.text('$' + formatCurrency(total4100Revenue), 105, yPosition, { align: 'right' });
    pdf.text('$' + formatCurrency(total4100Taxable), 145, yPosition, { align: 'right' });
    pdf.text('$' + formatCurrency(parseFloat(total4100Tax)), 185, yPosition, { align: 'right' });
    yPosition += 12;

    // 4900-Sales Discounts & Returns Section - SIMPLIFIED APPROACH
    checkPageBreak(20); // Extra space before this section

    pdf.text('4900-Discounts & Returns', margin, yPosition);
    yPosition += 8;

    // Render discount categories with manual spacing control
    discounts4900Categories.forEach(category => {
      const categoryName = category.split('-')[1] || category;
      const categoryData = formData[category];
      const gross = categoryData?.gross || '0.00';
      const taxable = categoryData?.taxable || '0.00';
      const tax = calculateSalesTax(parseFloat(taxable));

      const nameLines = pdf.splitTextToSize(categoryName, 45);

      // Check page break for this specific item
      checkPageBreak(nameLines.length * 6 + 8);

      // Draw category name (handle multi-line)
      if (nameLines.length > 1) {
        // Multi-line: draw all lines first
        nameLines.forEach((line, index) => {
          pdf.text(line, margin + 5, yPosition);
          yPosition += 6;
        });
        // Position numbers at the last line's Y position
        const numbersY = yPosition - 6;
        pdf.text('$' + formatCurrency(parseFloat(gross)), 105, numbersY, { align: 'right' });
        pdf.text('$' + formatCurrency(parseFloat(taxable)), 145, numbersY, { align: 'right' });
        pdf.text('$' + formatCurrency(parseFloat(tax)), 185, numbersY, { align: 'right' });
        yPosition += 4;
      } else {
        // Single line: simple positioning
        pdf.text(nameLines[0], margin + 5, yPosition);
        pdf.text('$' + formatCurrency(parseFloat(gross)), 105, yPosition, { align: 'right' });
        pdf.text('$' + formatCurrency(parseFloat(taxable)), 145, yPosition, { align: 'right' });
        pdf.text('$' + formatCurrency(parseFloat(tax)), 185, yPosition, { align: 'right' });
        yPosition += 8;
      }
    });

    // 4900-Sales Discounts & Returns Total - GUARANTEED NO OVERLAP
    checkPageBreak(20); // Ensure plenty of space before total
    yPosition += 8; // Extra space before total line


    pdf.text('Total', margin, yPosition);
    pdf.text('$' + formatCurrency(total4900Discounts), 105, yPosition, { align: 'right' });
    pdf.text('$' + formatCurrency(total4900Taxable), 145, yPosition, { align: 'right' });
    pdf.text('$' + formatCurrency(parseFloat(total4900Tax)), 185, yPosition, { align: 'right' });
    yPosition += 15; // Extra space after total

    // Total Income
    checkPageBreak(15);

    pdf.text('Total Income', margin, yPosition);
    pdf.text('$' + formatCurrency(totalIncome), 105, yPosition, { align: 'right' });
    pdf.text('$' + formatCurrency(totalTaxable), 145, yPosition, { align: 'right' });
    pdf.text('$' + formatCurrency(parseFloat(totalSalesTax)), 185, yPosition, { align: 'right' });
    yPosition += 15;

    // Tax Summary
    checkPageBreak(20);
    pdf.text('Tax Summary:', margin, yPosition);
    yPosition += 8;

    pdf.setFont(undefined, 'normal');
    pdf.text('4010 & 4020-Sales Tax on Compact Tobacco:', margin + 5, yPosition);
    pdf.text('$' + formatCurrency(parseFloat(compactTobaccoTax)), 185, yPosition, { align: 'right' });
    yPosition += 8;

    pdf.text('All other Sales Tax:', margin + 5, yPosition);
    pdf.text('$' + formatCurrency(parseFloat(otherTax)), 185, yPosition, { align: 'right' });
    yPosition += 10;


    pdf.text('Total Sales Tax:', margin + 5, yPosition);
    pdf.text('$' + formatCurrency(parseFloat(totalSalesTax)), 185, yPosition, { align: 'right' });
    yPosition += 15;

    // Certification
    checkPageBreak(50);
    pdf.setFontSize(12);
    pdf.setTextColor(0, 0, 0);
    pdf.text('Signature and Certification', 105, yPosition, { align: 'center' });
    yPosition += 12;

    pdf.setFontSize(10);
    pdf.setTextColor(0, 0, 0);

    const certificationLines = [
      'I certify that I am a duly authorized owner, partner, director, officer or',
      'representative of the licensee and that the statements made herein are true',
      'and correct to the best of my knowledge.'
    ];

    certificationLines.forEach(line => {
      checkPageBreak(8);
      pdf.text(line, margin, yPosition);
      yPosition += 6;
    });

    yPosition += 10;

    // Signature section
    if (signatureToUse) {
      try {
        checkPageBreak(50);
        pdf.text('Signature:', margin, yPosition);
        yPosition += 8;
        pdf.addImage(signatureToUse, 'PNG', margin, yPosition, 80, 30);
        yPosition += 45;
      } catch (error) {
        console.error('Error adding signature to PDF:', error);
        checkPageBreak(10);
        pdf.text('Signature: Provided during submission', margin, yPosition);
        yPosition += 10;
      }
    } else {
      checkPageBreak(10);
      pdf.text('Signature: Not provided', margin, yPosition);
      yPosition += 10;
    }

    // Printed Name and Title
    if (printedName) {
      checkPageBreak(8);
      pdf.text(`Printed Name: ${printedName}`, margin, yPosition);
      yPosition += 8;
    }

    if (title) {
      checkPageBreak(8);
      pdf.text(`Title: ${title}`, margin, yPosition);
      yPosition += 8;
    }

    checkPageBreak(8);
    pdf.text(`Date Signed: ${currentDate}`, margin, yPosition);
    yPosition += 15;

    return pdf;
  };

  // Helper function to convert FormData to object for debugging
  const formDataToObject = (formData) => {
    const object = {};
    for (const [key, value] of formData.entries()) {
      if (key === 'Signature' || key === 'PDF') {
        object[key] = `[File Blob - ${value.size} bytes]`;
      } else if (key === 'Body') {
        try {
          object[key] = JSON.parse(value);
        } catch (e) {
          object[key] = value;
        }
      } else {
        object[key] = value;
      }
    }
    return object;
  };

  // Function to handle PDF download from SuccessPage
  const handleDownloadPDF = () => {
    const pdf = generatePDF();
    const fileName = `Sales_Tax_Report_${retailerInfo.month}_${retailerInfo.year}_${taxpayerId || 'unknown'}.pdf`;
    pdf.save(fileName);
  };

  // Function to handle back to form
  const handleBackToForm = () => {
    setShowSuccessScreen(false);
  };

  // Signature functions
  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
      setIsSigned(false);
    }
  };

  const handleSignatureBegin = () => {
    setIsSigned(true);
  };

  const handleSignatureEnd = () => {
    // Get signature data immediately after signing
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      setSignatureDataURL(signatureData);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isSigned || (sigCanvas.current && sigCanvas.current.isEmpty())) {
      alert('Please provide a signature before submitting the report.');
      return;
    }

    if (!printedName.trim()) {
      alert('Please enter your printed name.');
      return;
    }

    if (!title.trim()) {
      alert('Please enter your title.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Get signature data and store it for PDF generation
      const signatureData = sigCanvas.current.getTrimmedCanvas().toDataURL('image/png');
      setSignatureDataURL(signatureData);

      // Generate PDF with signature passed in (state update is async, so state would still be old here)
      const pdf = generatePDF(signatureData);

      // Convert PDF to Blob and then to File
      const pdfBlob = pdfToBlob(pdf);
      const pdfFile = new File([pdfBlob], `Sales_Tax_Report_${retailerInfo.month}_${retailerInfo.year}_${taxpayerId || 'unknown'}.pdf`, {
        type: 'application/pdf'
      });

      // Format the data according to the required JSON structure
      const submissionData = {
        "4000 sales": [
          {
            "code": "4010",
            "gross": parseFloat(formData['4010-Sales Compact Discount Tobacco (NPM)']?.gross) || 0,
            "taxable": parseFloat(formData['4010-Sales Compact Discount Tobacco (NPM)']?.taxable) || 0
          },
          {
            "code": "4020",
            "gross": parseFloat(formData['4020-Sales Compact Premium Tobacco PM & NPM']?.gross) || 0,
            "taxable": parseFloat(formData['4020-Sales Compact Premium Tobacco PM & NPM']?.taxable) || 0
          },
          {
            "code": "4025",
            "gross": parseFloat(formData['4025-Sales Other Tobacco Products']?.gross) || 0,
            "taxable": parseFloat(formData['4025-Sales Other Tobacco Products']?.taxable) || 0
          },
          {
            "code": "4030",
            "gross": parseFloat(formData['4030-Sales Fuel']?.gross) || 0,
            "taxable": parseFloat(formData['4030-Sales Fuel']?.taxable) || 0
          },
          {
            "code": "4035",
            "gross": parseFloat(formData['4035-Sales Diesel Fuel']?.gross) || 0,
            "taxable": parseFloat(formData['4035-Sales Diesel Fuel']?.taxable) || 0
          },
          {
            "code": "4040",
            "gross": parseFloat(formData['4040-Sales Gift Shop']?.gross) || 0,
            "taxable": parseFloat(formData['4040-Sales Gift Shop']?.taxable) || 0
          },
          {
            "code": "4050",
            "gross": parseFloat(formData['4050-Sales Merchandise']?.gross) || 0,
            "taxable": parseFloat(formData['4050-Sales Merchandise']?.taxable) || 0
          },
          {
            "code": "4055",
            "gross": parseFloat(formData['4055-Non-Inv Merchandise']?.gross) || 0,
            "taxable": parseFloat(formData['4055-Non-Inv Merchandise']?.taxable) || 0
          }
        ],
        "4100 revenue": [
          {
            "code": "4110",
            "gross": parseFloat(formData['4110-Revenue ATM']?.gross) || 0,
            "taxable": parseFloat(formData['4110-Revenue ATM']?.taxable) || 0
          }
        ],
        "4900 sales": [
          {
            "code": "4910",
            "gross": parseFloat(formData['4910-Sales Discounts']?.gross) || 0,
            "taxable": parseFloat(formData['4910-Sales Discounts']?.taxable) || 0
          },
          {
            "code": "4920",
            "gross": parseFloat(formData['4920-Sales coupons']?.gross) || 0,
            "taxable": parseFloat(formData['4920-Sales coupons']?.taxable) || 0
          }
        ],
        // Moved printedName and title inside the Body object
        "printedName": printedName,
        "title": title
      };

      // Store submission data for PDF generation
      setSubmissionData(submissionData);

      // Format month for API (convert to number with leading zero)
      const formatMonthForAPI = (monthName) => {
        const monthMap = {
          'January': '01', 'February': '02', 'March': '03', 'April': '04',
          'May': '05', 'June': '06', 'July': '07', 'August': '08',
          'September': '09', 'October': '10', 'November': '11', 'December': '12'
        };
        return monthMap[monthName] || '01';
      };

      const formattedMonth = formatMonthForAPI(retailerInfo.month);
      const dateParam = `${formattedMonth}-${retailerInfo.year}`;

      // Create FormData object
      const formDataToSend = new FormData();

      // Add URL parameters as form data
      formDataToSend.append('taxpayer', taxpayerId);
      formDataToSend.append('reporttype', 'salestax');
      formDataToSend.append('date', dateParam);
      formDataToSend.append('test', 'true');

      // Add report month and year
      formDataToSend.append('reportMonth', retailerInfo.month);
      formDataToSend.append('reportYear', retailerInfo.year);

      // Add the JSON data as a string under 'Body' key (now includes printedName and title)
      formDataToSend.append('Body', JSON.stringify(submissionData));

      // REMOVED: formDataToSend.append('printedName', printedName);
      // REMOVED: formDataToSend.append('title', title);

      // Add signature as a file
      const signatureBlob = await fetch(signatureData).then(res => res.blob());
      formDataToSend.append('Signature', signatureBlob, 'signature.png');

      // Append PDF to FormData
      formDataToSend.append('PDF', pdfFile);

      // Append any optional supporting document uploads (same as StampsInventory/PacksInventory)
      attachments.forEach((file) => {
        formDataToSend.append('Receipts', withSanitizedFileName(file));
      });

      // Print form data for debugging
      console.log('=== FORM DATA TO SEND ===');
      const formDataObject = formDataToObject(formDataToSend);
      console.log('FormData contents:', JSON.stringify(formDataObject, null, 2));
      console.log('=== END FORM DATA ===');

      // Construct API URL
      const apiUrl = `${PBPUTILS}?taxpayer=${taxpayerId}&reporttype=salestax&date=${formattedMonth}-${retailerInfo.year}&test=true`;




    

      console.log('Submitting to API:', apiUrl);
      console.log('Submission data:', submissionData);

      // Submit to API
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formDataToSend
      });

      // First, get the response as text to handle both JSON and plain text responses
      const responseText = await response.text();

      console.log('API Response:', responseText);
      console.log('Report Month:', retailerInfo.month);
      console.log('Report Year:', retailerInfo.year);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}, message: ${responseText}`);
      }

      // Try to parse as JSON
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.warn('Response is not valid JSON, treating as plain text:', responseText);
        if (responseText.trim().toLowerCase() === 'true' || responseText.trim().toLowerCase() === 'success') {
          setShowSuccessScreen(true);
          return;
        } else {
          throw new Error(`Unexpected response from server: ${responseText}`);
        }
      }

      // Handle the JSON response
      if (result && result.Status === 'Success') {
        setShowSuccessScreen(true);
        return;
      } else if (result && result.Status === 'Error') {
        alert(result.Message || 'An error occurred while submitting the report.');
        return;
      } else {
        throw new Error('Unexpected response format from server');
      }

    } catch (error) {
      console.error('Error submitting form:', error);

      let errorMessage = 'Failed to submit sales tax report. Please try again.';
      if (error.message.includes('Invalid Taxpayer')) {
        errorMessage = 'Invalid Taxpayer ID. Please check your taxpayer information.';
      } else if (error.message.includes('Record already exist')) {
        errorMessage = error.message;
      } else if (error.message.includes('HTTP error')) {
        errorMessage = `Server error: ${error.message}`;
      } else if (error.message.includes('Unexpected response')) {
        errorMessage = error.message;
      }

      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };
  // Prepare report details for SuccessPage
  const reportDetails = {
    'Report Type': 'Sales Tax Report',
    'Taxpayer ID': taxpayerId,
    'Business': businessName,
    'Period': `${retailerInfo.month} ${retailerInfo.year}`,
    'Submitted': currentDate,

    'Total Sales Tax': `$${formatCurrency(parseFloat(totalSalesTax))}`,
    'Total Income': `$${formatCurrency(totalIncome)}`
  };

  // If showing success screen, render the SuccessPage component
  if (showSuccessScreen) {
    return (
      <SuccessPage
        title="Sales Tax Report Submitted Successfully!"
        subtitle={`Your sales tax report for ${retailerInfo.month} ${retailerInfo.year} has been successfully submitted.`}
        downloadButtonText="Download Sales Tax Report PDF"
        backButtonText="Back to Home"
        onDownload={handleDownloadPDF}
        onBack={handleBackToForm}
        reportDetails={reportDetails}
        contactInfo={{
          phone: "(785) 966-3060",
          email: "taxcommission@pbpnation.org",
          department: "Prairie Band Potawatomi Tax Commission"
        }}
      >
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <h3 className="font-semibold text-green-800 mb-2">Important Notes:</h3>
          <ul className="text-sm text-green-700 space-y-1">
            <li>• The report covers sales tax for the specified period</li>
            <li>• Total sales tax collected: ${formatCurrency(parseFloat(totalSalesTax))}</li>
            <li>• Total income reported: ${formatCurrency(totalIncome)}</li>
            <li>• Contact us if you need to make any corrections</li>
          </ul>
        </div>
      </SuccessPage>
    );
  }

  const renderCategoryRow = (category, label) => {
    // Check if this is a fuel or diesel row
    const isFuelRow = category === '4030-Sales Fuel' || category === '4035-Sales Diesel Fuel';
    
    // Determine the taxable value
    let taxableValue;
    if (isFuelRow) {
      // Fuel and diesel rows should always have taxable = 0
      taxableValue = '0.00';
    } else {
      // All other rows should mirror the gross amount
      taxableValue = formData[category]?.gross || '0.00';
    }

    // Handle gross amount changes - also update taxable for non-fuel rows
    const handleGrossChange = (e) => {
      const newGrossValue = e.target.value;
      handleInputChange(category, 'gross', newGrossValue);
      
      // If not a fuel row, automatically update taxable to match gross
      if (!isFuelRow) {
        handleInputChange(category, 'taxable', newGrossValue);
      }
    };

    return (
      <div key={category} className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center py-2 border-b">
        <label className="text-xs sm:text-sm text-gray-700 pl-2 sm:pl-4 col-span-1 sm:col-span-1">{label}</label>
        <input
          min="0"
          type="number"
          value={formData[category]?.gross || ''}
          onChange={handleGrossChange}
          className="border border-gray-300 px-2 sm:px-3 py-1 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-right text-xs sm:text-sm"
          placeholder="0.00"
        />
        <div className="bg-gray-100 px-2 sm:px-3 py-1 rounded border text-right text-xs sm:text-sm">
          ${formatCurrency(parseFloat(taxableValue) || 0)}
        </div>
        <div className="bg-gray-100 px-2 sm:px-3 py-1 rounded border text-right text-xs sm:text-sm">
          ${formatCurrency(calculateSalesTax(parseFloat(taxableValue) || 0))}
        </div>
      </div>
    );
  };

  const renderTotalRow = (label, gross, taxable, tax) => (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center py-2 border-b border-t font-semibold bg-gray-50">
      <div className="text-xs sm:text-sm text-gray-700 pl-2 sm:pl-2 col-span-1 sm:col-span-1">{label}</div>
      <div className="bg-gray-100 px-2 sm:px-3 py-1 rounded border text-right text-xs sm:text-sm">
        ${formatCurrency(gross)}
      </div>
      <div className="bg-gray-100 px-2 sm:px-3 py-1 rounded border text-right text-xs sm:text-sm">
        ${formatCurrency(taxable)}
      </div>
      <div className="bg-gray-100 px-2 sm:px-3 py-1 rounded border text-right text-xs sm:text-sm">
        ${formatCurrency(tax)}
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 bg-white">
      {/* Header */}
      <div className="text-center mb-6 sm:mb-8 border-b-2 border-[#840028] pb-4 sm:pb-6">
        <h1 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-[#840028]">Monthly Retailer Sales Tax Report</h1>
        <div className="font-bold mb-2 text-[#840028] text-sm sm:text-base">
          <div>Prairie Band Potawatomi Tax Commission</div>
          <div>16281 Q Road</div>
          <div>Mayetta, KS 66509</div>
        </div>
        <div className="italic mb-2 text-[#840028] text-sm sm:text-base">
          Reports and Payment due on the 10th of each Month
        </div>
        {taxpayerId && (
          <div className="text-xs sm:text-sm text-gray-600 mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-center gap-1 sm:gap-4">
            <span>Taxpayer ID: <strong>{taxpayerId}</strong></span>
            {businessName && (
              <span>Business: <strong>{businessName}</strong></span>
            )}
          </div>
        )}
      </div>

      {/* Form ID */}
      <div className="bg-gray-100 px-4 sm:px-6 py-2 border-b">
        <div className="flex justify-between items-center">
          <span className="font-semibold text-sm sm:text-base">Sales Tax Report</span>
          <span className="text-gray-600 text-xs sm:text-sm">Page 1</span>
        </div>
      </div>

      {/* Retailer Information Section */}
      <div className="border border-gray-300 p-3 sm:p-4 mb-4 sm:mb-6">
        {/* Distributor Row - At Top */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0 mb-3">
          <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Retailer:</label>
          <div className="px-3 py-2 border border-gray-300 bg-gray-50 sm:min-w-80 text-sm sm:text-base">
            {retailerInfo.retailer}
          </div>
        </div>

        {/* Month and Year Row */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-0 mb-3">
          <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">Month:</label>
          <select
            value={retailerInfo.month}
            onChange={(e) => handleRetailerInfoChange('month', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-32 sm:mr-8 text-sm sm:text-base"
          >
            {monthOptions.map(month => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>

          <label className="font-bold sm:w-12 sm:mr-4 text-[#840028] text-sm sm:text-base">Year:</label>
          <select
            value={retailerInfo.year}
            onChange={(e) => handleRetailerInfoChange('year', e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white sm:min-w-20 text-sm sm:text-base"
          >
            {yearOptions.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
        </div>

        <div className="hidden">
          <input
            type="text"
            value={retailerInfo.reportMonth}
            readOnly
          />
          <input
            type="text"
            value={retailerInfo.reportYear}
            readOnly
          />
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-0">
          <label className="font-bold sm:w-48 sm:mr-4 text-[#840028] text-sm sm:text-base">PBPN Tribal Retailer License Expiration Date:</label>
          <div className="px-3 py-2 border border-gray-300 bg-gray-50 sm:min-w-32 text-sm sm:text-base">
            {retailerInfo.expirationDate}
          </div>
        </div>
      </div>

      {/* Main Form Content */}
      <div className="p-4 sm:p-6">
        <h2 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-gray-800">
          Ordinary Income/Expense
        </h2>

        {/* Table Header */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 mb-3 sm:mb-4 border-b-2 border-t-2 border-gray-300 py-2">
          <div className="text-xs sm:text-sm font-semibold text-gray-700">Income</div>
          <div className="text-xs sm:text-sm font-semibold text-gray-700 text-right">Gross amount</div>
          <div className="text-xs sm:text-sm font-semibold text-gray-700 text-right">Taxable Amount</div>
          <div className="text-xs sm:text-sm font-semibold text-gray-700 text-right">6% Sales Tax</div>
        </div>

        {/* 4000-Sales Section */}
        <div className="mb-4 sm:mb-6">
          <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">4000-Sales</h3>

          {renderCategoryRow('4010-Sales Compact Discount Tobacco (NPM)', '4010-Sales Compact Discount Tobacco (NPM)')}
          {renderCategoryRow('4020-Sales Compact Premium Tobacco PM & NPM', '4020-Sales Compact Premium Tobacco PM & NPM')}
          {renderCategoryRow('4025-Sales Other Tobacco Products', '4025-Sales Other Tobacco Products')}
          {renderCategoryRow('4030-Sales Fuel', '4030-Sales Fuel')}
          {renderCategoryRow('4035-Sales Diesel Fuel', '4035-Sales Diesel Fuel')}
          {renderCategoryRow('4040-Sales Gift Shop', '4040-Sales Gift Shop')}
          {renderCategoryRow('4050-Sales Merchandise', '4050-Sales Merchandise')}
          {renderCategoryRow('4055-Non-Inv Merchandise', '4055-Non-Inv Merchandise')}

          {renderTotalRow('Total 4000-Sales', total4000Sales, total4000Taxable, parseFloat(total4000Tax))}
        </div>

        {/* 4100-Revenue Section */}
        <div className="mb-4 sm:mb-6">
          <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">4100-Revenue</h3>

          {renderCategoryRow('4110-Revenue ATM', '4110-Revenue ATM')}

          {renderTotalRow('Total 4100-Revenue', total4100Revenue, total4100Taxable, parseFloat(total4100Tax))}
        </div>

        {/* 4900-Sales Discounts & Returns Section */}
        <div className="mb-4 sm:mb-6">
          <h3 className="font-semibold text-gray-800 mb-2 text-sm sm:text-base">4900-Sales Discounts & Returns</h3>

          {renderCategoryRow('4910-Sales Discounts', '4910-Sales Discounts')}
          {renderCategoryRow('4920-Sales coupons', '4920-Sales coupons')}

          {renderTotalRow('Total 4900-Sales Discounts & Returns', total4900Discounts, total4900Taxable, parseFloat(total4900Tax))}
        </div>

        {/* Total Income */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 sm:gap-4 items-center py-3 border-t-2 border-b-2 border-gray-300 font-bold bg-gray-100">
          <div className="text-gray-800 text-xs sm:text-sm">Total Income</div>
          <div className="bg-white px-2 sm:px-3 py-1 sm:py-2 rounded border text-right text-xs sm:text-sm">
            ${formatCurrency(totalIncome)}
          </div>
          <div className="bg-white px-2 sm:px-3 py-1 sm:py-2 rounded border text-right text-xs sm:text-sm">
            ${formatCurrency(totalTaxable)}
          </div>
          <div className="bg-white px-2 sm:px-3 py-1 sm:py-2 rounded border text-right text-xs sm:text-sm">
            ${formatCurrency(parseFloat(totalSalesTax))}
          </div>
        </div>

        {/* Tax Summary Section */}
        <div className="mt-6 sm:mt-8 space-y-3 sm:space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8">
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 items-center">
                <label className="text-xs sm:text-sm font-semibold text-gray-700">
                  4010 & 4020-Sales Tax on Compact Tobacco:
                </label>
                <div className="bg-gray-100 px-2 sm:px-3 py-1 sm:py-2 rounded border text-right text-xs sm:text-sm">
                  ${formatCurrency(parseFloat(compactTobaccoTax))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 items-center">
                <label className="text-xs sm:text-sm font-semibold text-gray-700">
                  All other Sales Tax:
                </label>
                <div className="bg-gray-100 px-2 sm:px-3 py-1 sm:py-2 rounded border text-right text-xs sm:text-sm">
                  ${formatCurrency(parseFloat(otherTax))}
                </div>
              </div>
            </div>
          </div>

          {/* Total Sales Tax */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-8 border-t pt-3 sm:pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 items-center">
              <label className="text-sm sm:text-lg font-bold text-gray-800">
                Total Sales Tax:
              </label>
              <div className="bg-gray-100 px-2 sm:px-3 py-1 sm:py-2 rounded border text-right text-sm sm:text-lg font-bold">
                ${formatCurrency(parseFloat(totalSalesTax))}
              </div>
            </div>
          </div>
        </div>

        {/* Supporting Documents (Optional) - Same as StampsInventory/PacksInventory */}
        <div className="border border-gray-300 p-4 sm:p-6 mb-4 sm:mb-6 mt-6 sm:mt-8 rounded-lg bg-white">
          <h3 className="text-lg font-bold mb-3 sm:mb-4 text-center text-[#840028]">
            Supporting Documents (Optional)
          </h3>
          <p className="text-sm text-gray-700 mb-3 text-center">
            You may upload one or more files to support this report. This step is optional.
          </p>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upload Files:
            </label>
            <input
              type="file"
              multiple
              onChange={handleAttachmentChange}
              className="block w-full text-sm text-gray-700
                         file:mr-4 file:py-2 file:px-4
                         file:rounded-md file:border-0
                         file:text-sm file:font-semibold
                         file:bg-[#840028] file:text-white
                         hover:file:bg-[#6a0020]"
            />
            {attachments && attachments.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-700 mb-1">Selected files:</p>
                <ul className="space-y-2 text-xs text-gray-700">
                  {attachments.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-2 py-1"
                    >
                      <span className="truncate mr-2">{file.name}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(index)}
                        className="text-red-600 hover:text-red-800 text-[11px] font-semibold"
                      >
                        Delete
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Signature Section */}
        <div className="border border-gray-300 p-4 sm:p-6 mt-6 sm:mt-8 rounded-lg">
          <h3 className="text-base sm:text-lg font-bold mb-3 sm:mb-4 text-center text-gray-800">Signature</h3>

          <div className="mb-3 sm:mb-4">
            <label className="font-bold block mb-2 text-gray-700 text-sm sm:text-base">
              Sign below to certify the accuracy of this report:
            </label>
            <div
              id="signature-container"
              className="border-2 border-gray-300 rounded-lg p-3 sm:p-4 bg-white"
            >
              {canvasSize.width > 0 && (
                <SignatureCanvas
                  ref={sigCanvas}
                  penColor="#0000FF"
                  backgroundColor="rgb(255, 255, 255)"
                  canvasProps={{
                    width: canvasSize.width,
                    height: canvasSize.height,
                    className: 'signature-canvas w-full border border-gray-400 rounded bg-white cursor-crosshair'
                  }}
                  onBegin={handleSignatureBegin}
                  onEnd={handleSignatureEnd}
                  clearOnResize={false}
                />
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-3 sm:mb-4">
            <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
              {isSigned ? (
                <span className="text-green-600 font-bold">✓ Signature provided</span>
              ) : (
                <span className="text-red-600">Please sign above</span>
              )}
            </div>

            <button
              type="button"
              onClick={clearSignature}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition duration-200 text-xs sm:text-sm"
            >
              Clear Signature
            </button>
          </div>

          {/* Signature Information - Updated with required fields */}
          <div className="border-t pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Printed Name: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={printedName}
                  onChange={(e) => setPrintedName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                  placeholder="Enter printed name"
                  required
                />
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                  Title: <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-xs sm:text-sm"
                  placeholder="Enter title"
                  required
                />
              </div>
            </div>

            <div className="mt-3 sm:mt-4">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">
                Date Signed:
              </label>
              <input
                type="text"
                value={currentDate}
                readOnly
                className="w-full sm:w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-gray-100 font-medium text-gray-700 cursor-not-allowed text-xs sm:text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">
                This date is automatically set to today's date and cannot be modified
              </p>
            </div>
          </div>
        </div>

        {/* Certification */}
        <div className="border-t-2 border-b-2 border-gray-500 py-4 sm:py-6 my-6 sm:my-8 text-center italic text-gray-700 text-xs sm:text-sm">
          <p>
            I certify that I am a duly authorized owner, partner, director, officer or representative of the licensee and that the statements made herein are true and correct to the best of my knowledge.
          </p>
        </div>
      </div>

      {/* Footer Actions */}
      <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t">
        <div className="flex justify-center">
          <button
            onClick={handleSubmit}
            disabled={!isSigned || isSubmitting || !printedName.trim() || !title.trim()}
            className="px-6 sm:px-8 py-3 bg-[#840028] text-white font-medium rounded-md shadow-sm hover:bg-[#6a0020] focus:outline-none focus:ring-2 focus:ring-[#840028] focus:ring-offset-2 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto text-sm sm:text-base"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Submitting...
              </span>
            ) : (
              isSigned && printedName.trim() && title.trim() ? 'Submit Report' : 'Please Complete All Required Fields'
            )}
          </button>
        </div>
      </div>

      {/* Custom CSS for signature canvas */}
      <style jsx>{`
        .signature-canvas {
          cursor: crosshair !important;
          touch-action: none;
        }
        .signature-canvas:active {
          cursor: grabbing !important;
        }
        #signature-container {
          min-height: 150px;
        }
        
        @media (min-width: 640px) {
          #signature-container {
            min-height: 200px;
          }
        }
      `}</style>
    </div>
  );
};

export default SalesTaxReport;